import { MetricUnit } from '@/__generated__/graphql';
import { useFormatters } from '@/hooks/useFormatters';
import { BatteryLevelIcon } from '@components/BatteryLevelIcon';
import TruncatedTypography from '@components/TruncatedTypography';
import { MovementDataRenderer } from '@features/gateCommon';
import { Trans } from '@lingui/macro';
import { Box, Skeleton } from '@mui/material';
import { FC } from 'react';
import { PanelPaper } from './common';
import { ConnectionStateRenderer } from './dataComponents/ConnectionStateRenderer';

interface Props {
  movementId: string | null;
}

const CONNECTION_COLOR_MAP = {
  connected: '#0AEB0E',
  disconnected: 'grey',
};

export const PanelStatus: FC<Props> = ({ movementId }) => {
  const { formatMetricValue } = useFormatters();

  return (
    <PanelPaper
      sx={{
        gridArea: 'status',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 4,
      }}
    >
      {movementId && (
        <ConnectionStateRenderer
          movementId={movementId}
          render={({ lastConnection }) => {
            const connected = lastConnection?.[1] === 1;
            const connectionColor = CONNECTION_COLOR_MAP[connected ? 'connected' : 'disconnected'];
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <Box height={14} width={14} borderRadius={32} bgcolor={connectionColor} />
                <TruncatedTypography fontWeight="medium" lineHeight={1} pb="1px">
                  {connected ? <Trans>conectado</Trans> : <Trans>desconectado</Trans>}
                </TruncatedTypography>
              </Box>
            );
          }}
          fallback={() => <Skeleton>placeholder</Skeleton>}
        />
      )}

      {movementId && (
        <MovementDataRenderer
          movementId={movementId}
          render={({ gate }) => {
            const lastBattery = gate.movements2.lastBattery;
            const batteryLevel = lastBattery?.[1] ?? null;
            return (
              <Box display="flex" alignItems="center" gap="1px" pb="1px">
                <BatteryLevelIcon battery={batteryLevel ?? 50} />
                <TruncatedTypography fontWeight="medium" lineHeight={1}>
                  {batteryLevel
                    ? formatMetricValue(batteryLevel, { unit: MetricUnit.Percent, precision: 0 })
                    : 'N/D'}
                </TruncatedTypography>
              </Box>
            );
          }}
          fallback={() => <Skeleton>placeholder</Skeleton>}
        />
      )}
    </PanelPaper>
  );
};
